import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as SystemDefaultsAPI from 'api/SystemDefaults';
import { apiErrorMessage, notify, unwrap } from 'utils/notify';

// ───────────────────────────────────────────────────────────────────────────
// System Defaults store — the platform runtime-config rows behind
// /api/super/settings/ (BACKEND_CHANGES_ADMINPANEL.md §1).
//
// `rows` is keyed by the setting's `key` rather than held as an array: the page
// renders a fixed catalogue and looks each key up, and an absent key is a
// meaningful state ("using the code default"), not a missing record. A map
// makes both of those a direct lookup.
//
// Writes are per-key, so `savingKeys` is a set-like map instead of one global
// flag — saving several rows at once must not blank out the whole form.
// ───────────────────────────────────────────────────────────────────────────

const initialState = {
  rows: {},
  loading: false,
  loaded: false,
  savingKeys: {},
  error: null
};

const byKey = (list) =>
  (list || []).reduce((acc, row) => {
    if (row?.key) acc[row.key] = row;
    return acc;
  }, {});

const isAbort = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

// ── thunks ────────────────────────────────────────────────────────────────

const getSystemDefaults = createAsyncThunk('systemDefaults/getAll', async (signal, { rejectWithValue }) => {
  try {
    return byKey(await SystemDefaultsAPI.getAllSystemDefaults(signal));
  } catch (error) {
    // The page aborts its in-flight GET on unmount — not a failure worth a snackbar.
    if (isAbort(error)) return rejectWithValue(null);
    const message = apiErrorMessage(error, 'Failed to load system defaults.');
    notify.error(message);
    return rejectWithValue(message);
  }
});

// `exists` comes from whether the page currently holds a row for the key, so a
// first-time override POSTs and a subsequent edit PATCHes. The API layer still
// falls back to a create if the row vanished underneath us.
const saveSystemDefault = createAsyncThunk(
  'systemDefaults/save',
  async ({ key, value, description, exists, silent }, { rejectWithValue }) => {
    try {
      const res = await SystemDefaultsAPI.upsertSystemDefault({ key, value, description, exists });
      if (!silent) notify.success(`Saved ${key}.`);
      // The write echoes the row back; fall back to a synthesised one so the UI
      // still flips to "overridden" if the response body is thin.
      return unwrap(res) || { key, value, description };
    } catch (error) {
      const message = apiErrorMessage(error, `Failed to save ${key}.`);
      notify.error(message);
      return rejectWithValue({ key, message });
    }
  }
);

// Deleting the row is what "revert to default" means on this surface — there is
// no null-it-out write.
const resetSystemDefault = createAsyncThunk('systemDefaults/reset', async ({ key, silent }, { rejectWithValue }) => {
  try {
    await SystemDefaultsAPI.resetSystemDefault(key);
    if (!silent) notify.success(`${key} reverted to its code default.`);
    return { key };
  } catch (error) {
    const message = apiErrorMessage(error, `Failed to reset ${key}.`);
    notify.error(message);
    return rejectWithValue({ key, message });
  }
});

// Writes already bust the 300s Redis cache — this is the manual escape hatch.
const invalidateSystemDefaultCache = createAsyncThunk('systemDefaults/invalidateCache', async (key, { rejectWithValue }) => {
  try {
    await SystemDefaultsAPI.invalidateSystemDefaultCache(key);
    notify.success(`Cache dropped for ${key}.`);
    return { key };
  } catch (error) {
    const message = apiErrorMessage(error, `Failed to refresh the cache for ${key}.`);
    notify.error(message);
    return rejectWithValue({ key, message });
  }
});

// ── slice ─────────────────────────────────────────────────────────────────

const markSaving = (state, key, saving) => {
  if (saving) state.savingKeys[key] = true;
  else delete state.savingKeys[key];
};

const SystemDefaultsSlice = createSlice({
  name: 'systemDefaults',
  initialState,
  reducers: {
    resetState: () => initialState
  },
  extraReducers: {
    [getSystemDefaults.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getSystemDefaults.fulfilled]: (state, action) => {
      state.loading = false;
      state.loaded = true;
      state.rows = action.payload || {};
    },
    [getSystemDefaults.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error?.message;
      // Every key falls back to its code default anyway, so an empty map is the
      // honest render: the form shows baselines rather than a dead error page.
      state.rows = {};
    },

    [saveSystemDefault.pending]: (state, action) => {
      markSaving(state, action.meta.arg.key, true);
      state.error = null;
    },
    [saveSystemDefault.fulfilled]: (state, action) => {
      const row = action.payload;
      markSaving(state, action.meta.arg.key, false);
      if (row?.key) state.rows[row.key] = { ...(state.rows[row.key] || {}), ...row };
    },
    [saveSystemDefault.rejected]: (state, action) => {
      markSaving(state, action.meta.arg.key, false);
      state.error = action.payload?.message || action.error?.message;
    },

    [resetSystemDefault.pending]: (state, action) => {
      markSaving(state, action.meta.arg.key, true);
    },
    [resetSystemDefault.fulfilled]: (state, action) => {
      markSaving(state, action.meta.arg.key, false);
      delete state.rows[action.payload.key];
    },
    [resetSystemDefault.rejected]: (state, action) => {
      markSaving(state, action.meta.arg.key, false);
      state.error = action.payload?.message || action.error?.message;
    },

    [invalidateSystemDefaultCache.pending]: (state, action) => {
      markSaving(state, action.meta.arg, true);
    },
    [invalidateSystemDefaultCache.fulfilled]: (state, action) => {
      markSaving(state, action.payload.key, false);
    },
    [invalidateSystemDefaultCache.rejected]: (state, action) => {
      markSaving(state, action.meta.arg, false);
    }
  }
});

const { resetState } = SystemDefaultsSlice.actions;

export default SystemDefaultsSlice.reducer;

export { resetState, getSystemDefaults, saveSystemDefault, resetSystemDefault, invalidateSystemDefaultCache };
